import { Directive, HostListener, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { IdleService, StorageService, TokenService } from '@cargos/sprintpay-services';
import dayjs from 'dayjs';
import { Subject, combineLatest, map, switchMap, take, takeUntil } from 'rxjs';
import { JwtHelperService, SessionService } from 'src/app/shared/services';
import Swal from 'sweetalert2';

@Directive({
    standalone: true,
    selector: '[appCatchEvents]',
})
export class CatchEventsDirective implements OnDestroy {
    private userActivity: null | ReturnType<typeof setTimeout>;
    private endActivity: null | ReturnType<typeof setTimeout>;
    private userInactive: Subject<void> = new Subject();
    private isTokenRefreshed: null | ReturnType<typeof setTimeout>;
    private unsubscribe$: Subject<void>;
    private lastTokenCheck: number = 0;
    private tokenCheckInterval: number = 60000;
    private currentAccessToken: string;
    private currentRefreshToken: string;

    constructor(
        private idleService: IdleService,
        private storageService: StorageService,
        private router: Router,
        private tokenService: TokenService,
        private sessionService: SessionService,
        private jwtHelper: JwtHelperService
    ) {
        this.unsubscribe$ = new Subject<void>();
        this.subscribeAuthTokenAndRefreshToken();
        this.setIntervalToken();
        //SET USER ACTIVITY
        this.idleService.initListener();
        this.idleService.setActivity();
        this.listenerUserActivity();
        this.subscribeUpdateTokens();
        this.userInactive.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            this.validateToken();
        });
    }
    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    @HostListener('click', ['$event'])
    @HostListener('window:mousemove', ['$event'])
    onEvent(): void {
        //VALIDATE TIME OF INACTIVITY
        this.handleUserActivityWithThrottle();
        this.listenerUserActivity();
        this.idleService.setActivity();
    }

    //UPDATE USER ACTIVITY
    listenerUserActivity(): void {
        if (this.userActivity) {
            clearTimeout(this.userActivity);
        }
        this.userActivity = setTimeout(() => this.userInactive.next(undefined), 300000);
    }

    //VALIDATE EACH MINUTE TOKEN EXPIRATION
    setIntervalToken(): void {
        this.removeInterval();
        this.isTokenRefreshed = setInterval(() => {
            this.validateToken();
        }, 50000);
    }

    removeInterval(): void {
        if (this.isTokenRefreshed) {
            clearInterval(this.isTokenRefreshed);
        }
    }

    validateToken(): void {
        const isAccessExpired = this.jwtHelper.isTokenExpired(this.currentAccessToken);
        const isRefreshExpired = this.jwtHelper.isTokenExpired(this.currentRefreshToken);

        if (isAccessExpired || isRefreshExpired) {
            this.logout();
            return;
        }

        const decodedToken = this.tokenService.getDecodedToken();
        const customer = decodedToken.SPRINT_PAY_CUSTOMER;

        if (!customer) {
            return;
        }

        const lastMinuteToReview = 1;
        const currentDate = dayjs();
        const tokenExpDate = dayjs.unix(decodedToken.exp);

        if (tokenExpDate.diff(currentDate, 'minute') <= lastMinuteToReview) {
            const customerType = JSON.parse(customer).customerType;

            if (customerType !== 'GUEST') {
                if (this.customerHasActivity(currentDate)) {
                    this.refreshToken();
                } else {
                    this.removeInterval();
                    this.showContinueModal();
                }
            } else {
                this.refreshToken();
            }
        }
    }

    /**
     * VALIDATE CUSTOMER ACTIVITY
     **/
    customerHasActivity(currentDate: dayjs.Dayjs): boolean {
        var CURRENT_DATE = currentDate;
        return dayjs(CURRENT_DATE).diff(parseInt(this.idleService.getLastActivity()), 'minute') < 5;
    }

    logout(): void {
        this.sessionService
            .logout()
            .pipe(
                take(1),
                switchMap((response) => {
                    this.storageService.clearStorages();
                    this.router.navigate(['/']);
                    return response;
                })
            )
            .subscribe();
    }

    refreshToken(): void {
        this.sessionService.refreshToken().pipe(take(1)).subscribe();
    }

    showContinueModal(): void {
        let timerInterval;
        Swal.fire({
            title: 'Hey, pst pst!',
            html: 'You have been inactive for a long time. Would you like to continue <br> <b></b>',
            allowOutsideClick: false,
            focusConfirm: false,
            showCancelButton: true,
            customClass: {
                cancelButton: 'btn link',
            },
            cancelButtonText: 'No, Log out',
            confirmButtonText: 'Yes, continue',
            showCloseButton: false,
            reverseButtons: true,
            allowEscapeKey: false,
            timer: 60000,
            didOpen: () => {
                const timer = Swal.getPopup()?.querySelector('b');
                timerInterval = setInterval(() => {
                    const timeLeft = Swal.getTimerLeft();
                    const minutes = Math.floor((timeLeft || 0) / 60000);
                    const seconds = Math.floor(((timeLeft || 0) % 60000) / 1000);
                    if (timer) {
                        timer.textContent = `${
                            minutes.toString().padStart(2, '0') ?? '00'
                        }:${seconds.toString().padStart(2, '0') ?? '00'}`;
                    }
                }, 100);
            },
            willClose: () => {
                clearInterval(timerInterval);
            },
            didClose: () => {
                this.setIntervalToken();
            },
            didDestroy: () => {
                this.setIntervalToken();
            },
        }).then((result) => {
            if (result.value) {
                if (this.endActivity) {
                    clearTimeout(this.endActivity);
                }
                this.refreshToken();
            } else {
                if (this.endActivity) {
                    clearTimeout(this.endActivity);
                }
                this.logout();
            }
        });

        this.endActivity = setTimeout(() => {
            this.logout();
            Swal.close();
        }, 60000);
    }

    subscribeUpdateTokens(): void {
        this.tokenService
            .getRefreshToken$()
            .pipe(
                takeUntil(this.unsubscribe$),
                map(() => {
                    this.listenerUserActivity();
                    this.idleService.setActivity();
                    this.setIntervalToken();
                    if (this.endActivity) {
                        Swal.clickConfirm();
                    }
                })
            )
            .subscribe();
    }

    subscribeAuthTokenAndRefreshToken(): void {
        combineLatest([this.tokenService.getCurrentToken$(), this.tokenService.getRefreshToken$()])
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe({
                next: ([currentToken, refreshToken]) => {
                    this.currentAccessToken = currentToken;
                    this.currentRefreshToken = refreshToken;
                },
            });
    }

    private handleUserActivityWithThrottle(): void {
        const now = Date.now();
        if (now - this.lastTokenCheck > this.tokenCheckInterval) {
            this.lastTokenCheck = now;
            if (!this.currentAccessToken || !this.currentRefreshToken) {
                return;
            }
            this.validateToken();
        }
    }
}
